import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { admin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Состояние для свернутой навигации
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? saved === 'true' : false;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  const navSections = [
    {
      title: 'Аналитика',
      items: [
        {
          path: '/statistics',
          label: 'Статистика',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          )
        },
        {
          path: '/price-analytics',
          label: 'Цены',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          )
        },
      ]
    },
    {
      title: 'Каталог',
      items: [
        { 
          path: '/products', 
          label: 'Товары',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )
        },
        { 
          path: '/categories', 
          label: 'Категории',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          )
        },
        { 
          path: '/slides', 
          label: 'Слайды',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )
        },
      ]
    },
    {
      title: 'Продажи',
      items: [
        { 
          path: '/orders', 
          label: 'Заказы', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          )
        },
        { 
          path: '/sales', 
          label: 'Продажи', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )
        },
        { 
          path: '/reservations', 
          label: 'Резервы', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          )
        },
      ]
    },
    {
      title: 'Клиенты',
      items: [
        { 
          path: '/users', 
          label: 'Пользователи', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )
        },
        { 
          path: '/messages', 
          label: 'CRM', 
          comingSoon: true,
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )
        },
      ]
    },
    {
      title: 'Интеграции',
      items: [
        {
          path: '/integration/avito',
          label: 'Avito',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
            </svg>
          )
        },
        {
          path: '/sync-markup',
          label: 'LegitFamPro',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )
        },
      ]
    },
  ];

  // Отладочная информация
  React.useEffect(() => {
    const root = document.documentElement;
    console.log('Layout rendered, theme from context:', theme);
    console.log('HTML classes:', root.classList.toString());
    console.log('HTML has dark class:', root.classList.contains('dark'));
    console.log('HTML data-theme:', root.getAttribute('data-theme'));
    console.log('HTML style color-scheme:', root.style.colorScheme);
  }, [theme]);

  return (
    <div 
      className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden"
      data-theme={theme}
    >
      {/* Sidebar */}
      <aside className={`${isCollapsed ? 'w-20' : 'w-64'} bg-white dark:bg-gray-800 shadow-sm border-r border-gray-200 dark:border-gray-700 flex flex-col h-full flex-shrink-0 transition-all duration-300`}>
        <div className={`${isCollapsed ? 'px-4' : 'px-6'} py-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 flex items-center justify-between`}>
          {!isCollapsed && <h1 className="text-xl font-bold text-gray-900 dark:text-white">customdrop</h1>}
          <button
            onClick={toggleSidebar}
            className={`${isCollapsed ? 'mx-auto' : ''} p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors`}
            title={isCollapsed ? 'Развернуть навигацию' : 'Свернуть навигацию'}
          >
            {isCollapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto min-h-0">
          {navSections.map((section, sectionIndex) => (
            <div key={section.title} className={sectionIndex > 0 ? 'pt-2' : ''}>
              {!isCollapsed && (
                <div className="px-4 mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {section.title}
                  </h3>
                </div>
              )}
              {isCollapsed && sectionIndex > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 mb-2"></div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <div key={item.path} className="relative">
                    {item.comingSoon ? (
                      <div
                        className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-2.5 text-sm font-medium rounded-lg cursor-not-allowed transition-all ${
                          isActive(item.path)
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700'
                            : 'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`${isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {item.icon}
                          </span>
                          {!isCollapsed && <span>{item.label}</span>}
                        </div>
                        {!isCollapsed && (
                          <span className="text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                            Скоро
                          </span>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive(item.path)
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 shadow-sm'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <span className={isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}>
                          {item.icon}
                        </span>
                        {!isCollapsed && <span>{item.label}</span>}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className={`p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 space-y-2`}>
          {!isCollapsed && (
            <div className="mb-3 px-4 py-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Пользователь</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{admin?.email}</p>
            </div>
          )}
          
          {/* Theme Toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('=== THEME TOGGLE BUTTON CLICKED ===');
              console.log('Current theme from context:', theme);
              
              const root = document.documentElement;
              console.log('HTML classes BEFORE click:', root.classList.toString());
              console.log('HTML has dark BEFORE:', root.classList.contains('dark'));
              
              // Вызываем toggleTheme
              toggleTheme();
              
              // Проверяем сразу после вызова (используем новую тему, а не старое значение из замыкания)
              setTimeout(() => {
                // Получаем актуальную тему из localStorage, так как state может еще не обновиться
                const currentTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
                const actualTheme = currentTheme || 'light';
                
                console.log('HTML classes AFTER click (100ms):', root.classList.toString());
                console.log('HTML has dark AFTER:', root.classList.contains('dark'));
                console.log('HTML data-theme:', root.getAttribute('data-theme'));
                console.log('HTML style:', root.getAttribute('style') || 'none');
                console.log('Actual theme from localStorage:', actualTheme);
                
                // Принудительно проверяем и исправляем, если нужно
                const hasDark = root.classList.contains('dark');
                const shouldHaveDark = actualTheme === 'dark';
                const dataTheme = root.getAttribute('data-theme');
                
                if (hasDark !== shouldHaveDark || dataTheme !== actualTheme) {
                  console.warn('Theme mismatch detected, fixing...');
                  console.warn('  hasDark:', hasDark, 'shouldHaveDark:', shouldHaveDark);
                  console.warn('  dataTheme:', dataTheme, 'should be:', actualTheme);
                  
                  // Исправляем
                  const body = document.body;
                  root.classList.remove('dark', 'light');
                  if (body) {
                    body.classList.remove('dark', 'light');
                  }
                  
                  if (shouldHaveDark) {
                    root.classList.add('dark');
                    if (body) {
                      body.classList.add('dark');
                    }
                  }
                  root.setAttribute('data-theme', actualTheme);
                  root.style.removeProperty('color-scheme');
                  
                  console.log('Fixed! HTML classes:', root.classList.toString());
                }
              }, 100);
            }}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-center gap-2'} bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors`}
            title={theme === 'light' ? 'Переключить на темную тему' : 'Переключить на светлую тему'}
          >
            {theme === 'light' ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                {!isCollapsed && <span>Темная тема</span>}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {!isCollapsed && <span>Светлая тема</span>}
              </>
            )}
          </button>
          
          {!isCollapsed && (
            <>
              <Link
                to="/change-password"
                className="w-full block text-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Сменить пароль
              </Link>
              <button
                onClick={handleLogout}
                className="w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Выход
              </button>
            </>
          )}
          {isCollapsed && (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              title="Выход"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto h-full min-w-0 bg-gray-50 dark:bg-gray-900">
        <div className="px-6 py-6">
          {children}
        </div>
      </main>
    </div>
  );
};

